import { useState } from 'react';
import { canManageUnion } from '../../../shared/domain/unionAccess.js';
import { RefreshIconButton } from '../../../shared/ui/RefreshIconButton.jsx';
import { useUnionNotices } from '../hooks/useUnionNotices.js';

const EMPTY_FORM = { id: '', title: '', body: '', pinned: false };

function dateLabel(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function UnionNoticeBoard({ mobile = false, onNavigate }) {
  const notices = useUnionNotices();
  const editable = canManageUnion(notices.viewerRole);
  const [form, setForm] = useState(null);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [commentEditor, setCommentEditor] = useState(null);
  const [expandedCommentIds, setExpandedCommentIds] = useState(() => new Set());

  const update = (key, value) => setForm((current) => ({ ...(current || EMPTY_FORM), [key]: value }));
  const updateCommentDraft = (noticeId, value) => {
    setCommentDrafts((current) => ({ ...current, [noticeId]: value }));
  };
  const setCommentsExpanded = (noticeId, expanded) => {
    setExpandedCommentIds((current) => {
      const next = new Set(current);
      if (expanded) next.add(noticeId);
      else next.delete(noticeId);
      return next;
    });
  };
  const submit = async (event) => {
    event.preventDefault();
    if (await notices.save(form || EMPTY_FORM)) setForm(null);
  };
  const remove = async (item) => {
    if (!window.confirm(`“${item.title}” 공지를 삭제할까요?`)) return;
    if (await notices.remove(item.id)) {
      setForm((current) => current?.id === item.id ? null : current);
      setCommentsExpanded(item.id, false);
    }
  };
  const submitComment = async (event, noticeId) => {
    event.preventDefault();
    const editing = commentEditor?.noticeId === noticeId ? commentEditor : null;
    const body = editing ? editing.body : String(commentDrafts[noticeId] || '');
    const saved = await notices.saveComment({
      id: editing?.id || '',
      noticeId,
      body,
    });
    if (!saved) return;
    setCommentDrafts((current) => ({ ...current, [noticeId]: '' }));
    setCommentEditor((current) => current?.noticeId === noticeId ? null : current);
    setCommentsExpanded(noticeId, true);
  };
  const removeComment = async (comment) => {
    if (!window.confirm('이 댓글을 삭제할까요?')) return;
    if (await notices.removeComment(comment.id)) {
      setCommentEditor((current) => current?.id === comment.id ? null : current);
    }
  };

  if (notices.loading) {
    return (
      <section className={`union-notice-state ${mobile ? 'mobile' : ''}`}>
        <span />
        <p>유니온 공지를 불러오는 중입니다.</p>
      </section>
    );
  }

  return (
    <section className={`union-notice-board ${mobile ? 'mobile' : 'desktop'}`} aria-label="유니온 공지">
      <header className="union-notice-heading">
        <div>
          <p>UNION NOTICE</p>
          <h2>{notices.unionName || '유니온'} 공지</h2>
          <span>현재 가입 유니온에만 공개되는 운영 안내입니다.</span>
        </div>
        <div>
          <RefreshIconButton onClick={() => notices.refresh()} />
          {onNavigate ? <button type="button" className="union-notice-support-entry" onClick={() => onNavigate('support')}>문의하기</button> : null}
          {editable ? <button type="button" className="union-notice-create" onClick={() => setForm({ ...EMPTY_FORM })}>공지 작성</button> : null}
        </div>
      </header>

      {notices.notice.message ? <p className={`union-notice-feedback ${notices.notice.type}`} role="status">{notices.notice.message}</p> : null}

      {form ? (
        <form className="union-notice-editor" onSubmit={submit}>
          <header>
            <div><span>{form.id ? 'NOTICE EDIT' : 'NEW NOTICE'}</span><h3>{form.id ? '공지 수정' : '새 공지 작성'}</h3></div>
            <label><input type="checkbox" checked={form.pinned} onChange={(event) => update('pinned', event.target.checked)} /> 상단 고정</label>
          </header>
          <label><span>제목</span><input maxLength={120} value={form.title} onChange={(event) => update('title', event.target.value)} placeholder="공지 제목" /></label>
          <label><span>내용</span><textarea maxLength={5000} rows={mobile ? 7 : 9} value={form.body} onChange={(event) => update('body', event.target.value)} placeholder="유니온 멤버에게 전달할 내용을 입력하세요." /></label>
          <div className="union-notice-editor-actions">
            <button type="submit" disabled={Boolean(notices.working)}>{notices.working === 'save' ? '저장 중…' : '저장'}</button>
            <button type="button" onClick={() => setForm(null)} disabled={Boolean(notices.working)}>닫기</button>
            {form.id ? <button type="button" className="danger" onClick={() => remove(form)} disabled={Boolean(notices.working)}>{notices.working === `delete:${form.id}` ? '삭제 중…' : '삭제'}</button> : null}
          </div>
        </form>
      ) : null}

      <div className="union-notice-list">
        {notices.notices.map((item) => {
          const comments = Array.isArray(item.comments) ? item.comments : [];
          const editing = commentEditor?.noticeId === item.id ? commentEditor : null;
          const commentBody = editing ? editing.body : String(commentDrafts[item.id] || '');
          const commentSaving = notices.working === `comment-save:${item.id}`;
          const commentsExpanded = expandedCommentIds.has(item.id) || Boolean(editing);

          return (
            <article className={item.pinned ? 'pinned' : ''} key={item.id}>
              <header className="union-notice-item-head">
                <div className="union-notice-item-title">{item.pinned ? <b>고정</b> : null}<h3>{item.title}</h3></div>
                <div className="union-notice-item-meta">
                  <time dateTime={item.updatedAt || item.createdAt || ''}>{dateLabel(item.updatedAt || item.createdAt)}</time>
                  {editable ? (
                    <div className="union-notice-actions">
                      <button type="button" onClick={() => setForm({ id: item.id, title: item.title, body: item.body, pinned: item.pinned })}>수정</button>
                      <button type="button" className="danger" onClick={() => remove(item)} disabled={Boolean(notices.working)}>{notices.working === `delete:${item.id}` ? '삭제 중…' : '삭제'}</button>
                    </div>
                  ) : null}
                </div>
              </header>
              <p>{item.body}</p>

              <section className={`union-notice-comments ${commentsExpanded ? 'expanded' : 'collapsed'}`} aria-label={`${item.title} 댓글`}>
                <header>
                  <button
                    type="button"
                    className="union-notice-comment-toggle"
                    aria-expanded={commentsExpanded}
                    onClick={() => setCommentsExpanded(item.id, !commentsExpanded)}
                  >
                    <span><strong>댓글</strong><em>{comments.length}</em></span>
                    <b>{commentsExpanded ? '접기' : '펼치기'}</b>
                  </button>
                </header>
                {commentsExpanded ? (
                  <>
                    <div className="union-notice-comment-list">
                      {comments.map((comment) => {
                        const own = comment.authorUserId === notices.viewerUserId;
                        const canDelete = own || editable;
                        return (
                          <article key={comment.id} className="union-notice-comment">
                            <header>
                              <div><strong>{comment.authorName || '멤버'}</strong>{own ? <span>내 댓글</span> : null}</div>
                              <div className="union-notice-comment-meta">
                                <time dateTime={comment.updatedAt || comment.createdAt || ''}>{dateLabel(comment.updatedAt || comment.createdAt)}</time>
                                {own || canDelete ? (
                                  <div className="union-notice-comment-actions">
                                    {own ? <button type="button" onClick={() => { setCommentsExpanded(item.id, true); setCommentEditor({ id: comment.id, noticeId: item.id, body: comment.body }); }}>수정</button> : null}
                                    {canDelete ? <button type="button" className="danger" onClick={() => removeComment(comment)} disabled={Boolean(notices.working)}>{notices.working === `comment-delete:${comment.id}` ? '삭제 중…' : '삭제'}</button> : null}
                                  </div>
                                ) : null}
                              </div>
                            </header>
                            <p>{comment.body}</p>
                          </article>
                        );
                      })}
                      {!comments.length ? <p className="union-notice-comment-empty">아직 댓글이 없습니다.</p> : null}
                    </div>

                    <form className="union-notice-comment-form" onSubmit={(event) => submitComment(event, item.id)}>
                      <textarea
                        maxLength={1000}
                        rows={2}
                        value={commentBody}
                        onChange={(event) => {
                          if (editing) setCommentEditor((current) => ({ ...current, body: event.target.value }));
                          else updateCommentDraft(item.id, event.target.value);
                        }}
                        placeholder={editing ? '댓글을 수정하세요.' : `${notices.viewerNickname || '멤버'}님, 댓글을 입력하세요.`}
                      />
                      <div>
                        <small>{commentBody.length}/1000</small>
                        {editing ? <button type="button" onClick={() => setCommentEditor(null)} disabled={Boolean(notices.working)}>수정 취소</button> : null}
                        <button type="submit" disabled={Boolean(notices.working) || !commentBody.trim()}>{commentSaving ? '저장 중…' : editing ? '댓글 수정' : '댓글 등록'}</button>
                      </div>
                    </form>
                  </>
                ) : null}
              </section>
            </article>
          );
        })}
        {!notices.notices.length ? (
          <div className="union-notice-empty">
            <strong>등록된 공지가 없습니다.</strong>
            <p>{editable ? '첫 공지를 작성해 멤버에게 운영 내용을 알려주세요.' : '관리자가 공지를 등록하면 여기에 표시됩니다.'}</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
